import { Button, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import { useOfferAggregateBidMutation } from '@/features/bidding/hooks/useOfferAggregateBidMutation'
import type { AggregateBidOfferRequest } from '@/features/bidding/types/aggregateBidOffer'

type CreateAggregateBidsButtonProps = {
  request: AggregateBidOfferRequest
  bidCount: number
  onSuccess?: () => void
}

function CreateAggregateBidsButton({ request, bidCount, onSuccess }: Readonly<CreateAggregateBidsButtonProps>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { offerAggregateBid, isPending } = useOfferAggregateBidMutation()

  const handleClick = async () => {
    try {
      await offerAggregateBid(request)
      pushAlert({
        message: t('bidding.bidding_cascading.create_bids.success.text', { count: bidCount }),
        severity: 'success',
        title: t('bidding.bidding_cascading.create_bids.success.title'),
      })
      onSuccess?.()
    } catch {
      // Error is already handled by useOfferAggregateBidMutation onError callback
    }
  }

  const isDisabled = bidCount === 0 || isPending

  return (
    <Button data-testid="create-bids-button" disabled={isDisabled} variant="contained" onClick={handleClick}>
      {isPending ? (
        <CircularProgress color="inherit" size={20} />
      ) : (
        t('bidding.bidding_cascading.create_bids.button', { count: bidCount })
      )}
    </Button>
  )
}

export default CreateAggregateBidsButton
